import { ResourceId } from 'framework/crud';
import { saveAs } from 'file-saver';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useCompositeDocumentStorageContext } from './CompositeDocumentStorageContext';
import { useOpportunityDocumentService, useOpportunityId } from 'domain/shared/opportunity';
import {
  normalizeStoragePath,
  normalizeStoragePathWithSlash,
  useDocumentStorageService,
} from 'domain/shared/documentStorage';
import {
  CROP_SIZES,
  getFilePreview,
  getFileType,
  getFileTypeFromName,
  RESIZE_SIZES,
  useArtifactDownloadHelper,
  useArtifactPreviewHelper,
  useArtifactService,
} from 'domain/shared/artifact';
import { useDebouncedBatchReceiver } from 'domain/shared/artifact/receivers';

export const ARTIFACTS_STORAGE_KEY = 'artifacts';
export const DOCUMENTS_STORAGE_KEY = 'documents';

export interface DocumentIdentifier {
  storage: string;
  id: string;
}

export interface DocumentFullInfo {
  id: DocumentIdentifier;
  title: string;
  type: string;
  hasPreview?: boolean;
  thumbnails?: Dictionary<string>;
  metadata?: Dictionary<unknown>;
}

export const useCompositeDocumentStorageService = (isDocumentStorageAvailableParam?: boolean) => {
  const entityId = useOpportunityId();
  const { isDocumentStorageAvailable: isDocumentStorageAvailableFromContext } = useCompositeDocumentStorageContext();
  const isDocumentStorageAvailable = isDocumentStorageAvailableParam ?? isDocumentStorageAvailableFromContext;

  const { collect } = useDebouncedBatchReceiver();
  const { addMessage } = useMessageService();
  const { getDocuments, generatePresignedLink, downloadFileContent, commitDocuments } = useOpportunityDocumentService();
  const {
    artifacts,
    setArtifactsToStore,
    uploadArtifactRaw: uploadArtifactDocumentStorage,
  } = useDocumentStorageService();
  const { getArtifactsDataByIds, getThumbnail, uploadArtifact } = useArtifactService();
  const { download } = useArtifactDownloadHelper();
  const { isPreviewAvailable } = useArtifactPreviewHelper();

  const getSubCacheKey = (identifier: DocumentIdentifier) => `${identifier.storage}-${identifier.id}`;

  const convertDocumentStorageResponse = (doc: any, customMetadata?: Dictionary<unknown>): DocumentFullInfo => {
    const documentType = getFileTypeFromName(doc.metadata.fileName);
    return {
      id: {
        storage: DOCUMENTS_STORAGE_KEY,
        id: doc.path,
      },
      title: doc.metadata.fileName || 'Document',
      type: documentType,
      hasPreview: isPreviewAvailable(documentType),
      metadata: { ...(doc.metadata || {}), ...(customMetadata || {}) },
    };
  };

  const convertKernelStorageResponse = (doc: any, customMetadata?: Dictionary<unknown>): DocumentFullInfo => ({
    id: {
      storage: ARTIFACTS_STORAGE_KEY,
      id: doc.id,
    },
    title: doc.title,
    type: doc.type,
    hasPreview: doc.hasPreview,
    thumbnails: doc.thumbnails,
    metadata: { ...doc, ...(customMetadata || {}) },
  });

  const getDocumentsInfo = async (identifiers: DocumentIdentifier[]): Promise<DocumentFullInfo[]> => {
    const documentIds: string[] = [];
    const artifactIds: string[] = [];
    const cachedData: DocumentFullInfo[] = [];

    identifiers.forEach(documentId => {
      if (isDocumentStorageAvailable && documentId.storage === DOCUMENTS_STORAGE_KEY) {
        const artifactKey = getSubCacheKey(documentId);
        const fileCachedValue = artifacts?.[artifactKey];

        if (fileCachedValue) {
          cachedData.push(fileCachedValue);
        } else if (!documentId.id.startsWith('/temp/')) {
          documentIds.push(documentId.id);
        } else {
          addMessage(
            new ErrorMessage(
              `Document with id ${documentId.id} is not committed yet and cannot be fetched from Redux storage`,
            ),
          );
        }
      } else if (documentId.storage === ARTIFACTS_STORAGE_KEY) {
        artifactIds.push(documentId.id);
      }
    });

    if (documentIds.length > 0 && entityId == null) {
      return cachedData;
    }

    const requests = [
      documentIds.length > 0 && entityId != null
        ? getDocuments(entityId as ResourceId, documentIds)
        : Promise.resolve({ result: [] }),
      artifactIds.length > 0 ? getArtifactsDataByIds(artifactIds) : Promise.resolve([]),
    ];

    const data: DocumentFullInfo[] = await Promise.all(requests).then(([opportunityDocuments, kernelDocuments]) => {
      const artifactoryResultDocs: DocumentFullInfo[] = kernelDocuments.map((doc: any) =>
        convertKernelStorageResponse(doc),
      );
      const opportunityResultDocs: DocumentFullInfo[] = opportunityDocuments.result.map((doc: any) =>
        convertDocumentStorageResponse(doc),
      );

      if (opportunityResultDocs.length > 0) {
        setArtifactsToStore(
          opportunityResultDocs.reduce((acc, doc) => ({ ...acc, [getSubCacheKey(doc.id)]: doc }), {}),
        );
      }

      return [...artifactoryResultDocs, ...opportunityResultDocs];
    });

    return Promise.resolve([...cachedData, ...data]);
  };

  const getDocumentsInfoDebounced = (identifiers: DocumentIdentifier[]) => {
    return identifiers.length > 0
      ? collect<DocumentIdentifier, DocumentFullInfo>({
          batchKey: `composite-documents:${entityId ?? 'unknown'}`,
          keys: identifiers,
          getKeyId: identifier => getSubCacheKey(identifier),
          fetchBatch: ids => getDocumentsInfo(ids),
          getResultKeyId: document => getSubCacheKey(document.id),
        })
      : Promise.resolve([]);
  };

  const getDocumentThumbnail = async (documentId: DocumentIdentifier, cropSize: string) => {
    if (documentId.storage === ARTIFACTS_STORAGE_KEY) {
      return getThumbnail(documentId.id, cropSize);
    }

    if (documentId.storage === DOCUMENTS_STORAGE_KEY) {
      const artifactKey = getSubCacheKey(documentId);
      const thumbnailUrl = artifacts?.[artifactKey]?.thumbnails?.[cropSize];

      if (thumbnailUrl) {
        return Promise.resolve(thumbnailUrl);
      }

      if (cropSize === RESIZE_SIZES.SIZE_1024 && entityId != null) {
        return generatePresignedLink(entityId as ResourceId, normalizeStoragePath(documentId.id));
      }

      return Promise.resolve(null);
    }

    return Promise.reject('Unable to get thumbnail for document');
  };

  const downloadDocument = async (documentId: DocumentIdentifier, filename?: string) => {
    if (documentId.storage === ARTIFACTS_STORAGE_KEY) {
      return download(documentId.id, filename);
    }

    if (documentId.storage === DOCUMENTS_STORAGE_KEY && entityId != null) {
      return downloadFileContent(entityId as ResourceId, normalizeStoragePathWithSlash(documentId.id)).then(blob => {
        saveAs(blob, filename);
      });
    }

    return Promise.reject('Unable to download document');
  };

  const uploadDocument = async (params: {
    file: File;
    artifactTypeId: string | number | Blob;
    signal?: AbortSignal;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  }) => {
    if (isDocumentStorageAvailable) {
      return uploadArtifactDocumentStorage(params).then(async data => {
        const filePreview = await getFilePreview(params.file);
        const type = getFileType(params.file);

        const newUploadedId = {
          id: data.id,
          storage: DOCUMENTS_STORAGE_KEY,
        };

        setArtifactsToStore({
          [getSubCacheKey(newUploadedId)]: {
            id: newUploadedId,
            title: params.file.name,
            type,
            thumbnails: {
              [CROP_SIZES.SIZE_100]: filePreview,
            },
            metadata: { ...data },
          },
        });

        return newUploadedId;
      });
    }

    return uploadArtifact(params).then(uploadedId => ({
      id: uploadedId,
      storage: ARTIFACTS_STORAGE_KEY,
    }));
  };

  const commitDocument = async (documentIds: string[], documentType: string): Promise<string[]> => {
    if (!isDocumentStorageAvailable || entityId == null) {
      return Promise.resolve(documentIds);
    }

    const [readyDocs, docsForCommit] = (documentIds || []).reduce(
      ([processedDocs, newDocs], doc) => {
        if (doc.startsWith('/temp/')) newDocs.push(doc);
        else processedDocs.push(doc);
        return [processedDocs, newDocs];
      },
      [[], []] as string[][],
    );

    if (docsForCommit.length === 0) {
      return Promise.resolve(documentIds);
    }

    const artifactPaths = await commitDocuments(entityId as ResourceId, {
      paths: docsForCommit,
      metadata: {
        documentType,
        source: 'crm-ui',
      },
    });

    return Promise.resolve([...readyDocs, ...artifactPaths.paths]);
  };

  return {
    getDocumentsInfo: getDocumentsInfoDebounced,
    getDocumentThumbnail,
    downloadDocument,
    uploadDocument,
    commitDocument,
  };
};
